import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { WaterDrop } from '@mui/icons-material';

interface WeatherSummaryProps {
    minTemp: number;
    maxTemp: number;
    precipitation: number;
    description: string;
    iconOnly?: boolean;
}

const getWeatherImageSrc = (description: string): string => {
    const desc = description.toLowerCase();
    if (desc.includes('rain') || desc.includes('drizzle')) return '/assets/weather/rainy.webp';
    if (desc.includes('snow') || desc.includes('sleet')) return '/assets/weather/snowy.webp';
    if (desc.includes('storm')) return '/assets/weather/stormy.webp';
    if (desc.includes('partly cloudy')) return '/assets/weather/partly_cloudy.webp';
    if (desc.includes('cloud') || desc.includes('overcast')) return '/assets/weather/cloudy.webp';
    if (desc.includes('fog')) return '/assets/weather/foggy.webp';
    return '/assets/weather/clear_sky.webp';
};

const formatPrecipitation = (value: number): string => {
    if (value < 1) {
        return (Math.round(value * 10) / 10).toString();
    }
    return Math.round(value).toString();
};

const WeatherSummary: React.FC<WeatherSummaryProps> = ({
    minTemp,
    maxTemp,
    precipitation,
    description,
    iconOnly = false
}) => {
    const iconSrc = getWeatherImageSrc(description);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title={description} arrow>
                <Box
                    component="img"
                    src={iconSrc}
                    alt={description}
                    sx={{
                        width: 32,
                        height: 32,
                        objectFit: 'contain'
                    }}
                />
            </Tooltip>
            {!iconOnly && (
                <Box>
                    <Typography variant="body2" className="serif-text" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                        {Math.round(maxTemp)}° / {Math.round(minTemp)}°
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.8 }}>
                        <WaterDrop sx={{ width: 12, height: 12, mr: 0.3, color: '#4fc3f7' }} />
                        <Typography variant="caption" className="serif-text" sx={{ fontWeight: 500 }}>
                            {formatPrecipitation(precipitation)}mm
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WeatherSummary;
